import React, { useCallback, useEffect, useMemo, useState } from "react";
import { adminApi } from "@/api/adminOneClient";

/**
 * CareLogix™ Uganda 🇺🇬 Phase 2 Real Infrastructure Activation™ — AdminOne Command Page (Phase 24).
 *
 * Strictly additive. AdminOne-only. JWT-protected by upstream <AdminProtectedRoute>.
 * Public ORACLE markets-intelligence positioning untouched.
 *
 * Tabs (all functional — no dead tabs):
 *   • LIVE MAP         — jurisdiction grid (Uganda LIVE, Canada NEXT)
 *   • UGANDA PHASE 2   — pillar overview cards
 *   • ALL SERVICES     — canonical Uganda service registry from the backend (33 services)
 *   • FDCE             — Forward Deployed Continuity Engineers Uganda
 *   • COMPLIANCE       — safety guardrails surfaced from backend `/status` payload
 */

const ICON = "🇺🇬";

const FALLBACK_COUNTRIES = [
  { flag: "🇺🇸", country: "United States", status: "ACTIVE" },
  { flag: "🇳🇬", country: "Nigeria",        status: "ACTIVE" },
  { flag: "🇷🇼", country: "Rwanda",         status: "ACTIVE" },
  { flag: "🇰🇪", country: "Kenya",          status: "ACTIVE" },
  { flag: "🇬🇭", country: "Ghana",          status: "ACTIVE" },
  { flag: "🇺🇬", country: "Uganda",         status: "ACTIVE" },
  { flag: "🇨🇦", country: "Canada",         status: "NEXT" },
];

const UGANDA_PILLARS = [
  ["CareLabs™ Uganda",       "Patient → Clinic → Lab → Result → Provider inside one CareThread."],
  ["CareRx™ Uganda",         "Prescription verification, stock visibility, counterfeit detection, medication continuity."],
  ["CareRide™ Uganda",       "HC III → HC IV → District Hospital → Regional Referral without losing the patient."],
  ["CareSupply™ Uganda",     "Shortages become visible before stockouts occur."],
  ["CareCompanion™ Uganda",  "Homecare, post-discharge, chronic follow-up, maternal follow-up, elder care."],
  ["Family Safety™ Uganda",  "Diaspora families support loved ones at home with visibility and alerts."],
  ["CareThreads™ Uganda",    "One timeline, one patient journey, one continuity record."],
  ["EvidenceOS™ Uganda",     "Every specimen, referral, delivery, home visit, and escalation gets an audit anchor."],
  ["FDCE™ Uganda",           "Deploy, configure, integrate, train, monitor adoption, drive closure rates."],
];

const TABS = [
  { id: "live-map",       label: "LIVE MAP" },
  { id: "uganda-phase2",  label: "UGANDA PHASE 2" },
  { id: "all-services",   label: "ALL SERVICES" },
  { id: "fdce",           label: "FDCE" },
  { id: "compliance",     label: "COMPLIANCE" },
];

export default function UgandaPhase2ActivationCommand() {
  const [tab, setTab] = useState("live-map");
  const [status, setStatus] = useState(null);
  const [liveMap, setLiveMap] = useState(null);
  const [activations, setActivations] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");

  const loadAll = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const [s, m, a] = await Promise.all([
        adminApi.ugandaPhase2Status(),
        adminApi.ugandaPhase2LiveMap(),
        adminApi.ugandaPhase2Activations(),
      ]);
      setStatus(s.data);
      setLiveMap(m.data);
      setActivations(a.data?.items || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Uganda Phase 2 load failed");
    } finally { setBusy(false); }
  }, []);

  const reseed = useCallback(async () => {
    setBusy(true); setErr(""); setInfo("");
    try {
      const { data } = await adminApi.ugandaPhase2Seed();
      setInfo(`Seeded ${data.service_count} services. Uganda LIVE + ACTIVE.`);
      await loadAll();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "reseed_failed");
    } finally { setBusy(false); }
  }, [loadAll]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const countries = useMemo(() => liveMap?.jurisdictions || FALLBACK_COUNTRIES, [liveMap]);
  const guardrails = useMemo(() => status?.guardrails || [
    "No autonomous clinical closure",
    "Human approval for maternal/referral milestones",
    "EvidenceOS anchor for every event",
    "Manual fallback for emergency movement",
    "NDA-aware CareRx routing",
    "Canada remains Next",
    "AdminOne governance required",
    "District visibility required",
  ], [status]);

  return (
    <div style={S.canvas} data-testid="uganda-phase2-page">
      {/* Governance Banner (inline; additive without depending on missing component) */}
      <div style={S.banner} data-testid="uganda-phase2-banner">
        <span style={S.bannerDot}>●</span>
        <span style={S.bannerText}>
          <strong>AdminOne Governance Shell</strong> · Uganda {ICON} Phase 2 Real Infrastructure Activation™ ·
          Human-confirm safety preserved · No autonomous clinical closure
        </span>
      </div>

      <header style={S.header} data-testid="uganda-phase2-header">
        <div>
          <h1 style={S.title}>CareLogix™ Uganda {ICON} Phase 2 Activation™</h1>
          <p style={S.subtitle}>
            Uganda is now live on the jurisdiction map and operationally wired across the full CareLogix
            service registry. Canada remains <b style={{ color: "#c084fc" }}>NEXT</b>.
          </p>
        </div>
        <div style={S.headerActions}>
          <span style={S.livePill} data-testid="uganda-phase2-live-pill">UGANDA LIVE + ACTIVE</span>
          <button
            type="button"
            onClick={loadAll}
            disabled={busy}
            style={S.refreshBtn}
            data-testid="uganda-phase2-refresh"
          >{busy ? "…" : "↻ Refresh"}</button>
          <button
            type="button"
            onClick={reseed}
            disabled={busy}
            style={S.reseedBtn}
            data-testid="uganda-phase2-reseed"
            title="Force re-seed Uganda Phase 2 service registry (HMAC-anchored, idempotent)"
          >Re-seed</button>
        </div>
      </header>

      {err && <div style={S.errBanner} data-testid="uganda-phase2-error">⚠ {err}</div>}
      {info && <div style={S.infoBanner} data-testid="uganda-phase2-info">✓ {info}</div>}

      <nav style={S.tabs} role="tablist" aria-label="Uganda Phase 2 sections">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            data-testid={`uganda-phase2-tab-${t.id}`}
            style={{
              ...S.tab,
              color: tab === t.id ? "#c5a059" : "#94a3b8",
              borderBottomColor: tab === t.id ? "#c5a059" : "transparent",
            }}
          >{t.label}</button>
        ))}
      </nav>

      <main style={S.main}>
        {/* ─────── LIVE MAP ─────── */}
        {tab === "live-map" && (
          <section style={S.panel} data-testid="uganda-phase2-section-live-map">
            <h2 style={S.heading}>Jurisdiction-Aware Healthcare Access Infrastructure</h2>
            <p style={S.bodySmall}>
              Active cluster: <b style={S.gold}>{liveMap?.active_network || status?.active_cluster || "East Africa Continuity Network™"}</b>.
              Uganda status: <b style={S.green}>{liveMap?.uganda || "LIVE_ACTIVE"}</b>.
              Canada: <b style={S.purple}>{liveMap?.canada || "NEXT_LOCKED"}</b>.
            </p>
            <div style={S.countryGrid}>
              {countries.map(({ flag, country, status: cs }) => (
                <div key={country} style={S.countryCard} data-testid={`uganda-phase2-country-${country.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <span style={S.flag}>{flag}</span>
                  <strong>{country}</strong>
                  <span style={(cs || "").toUpperCase() === "ACTIVE" ? S.greenPill : S.purplePill}>
                    {(cs || "—").toUpperCase()}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ─────── UGANDA PHASE 2 ─────── */}
        {tab === "uganda-phase2" && (
          <section style={S.panel} data-testid="uganda-phase2-section-overview">
            <h2 style={S.heading}>Real Infrastructure Activation™</h2>
            <p style={S.bodySmall}>
              Nine operational pillars are activated for Uganda {ICON}. Every activation is anchored in
              EvidenceOS™ and governed by AdminOne. Human approval is enforced for maternal &amp; referral
              milestones.
            </p>
            <div style={S.grid}>
              {UGANDA_PILLARS.map(([name, desc]) => (
                <div key={name} style={S.card} data-testid={`uganda-phase2-pillar-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <strong style={S.gold}>{name}</strong>
                  <p style={S.bodySmall}>{desc}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ─────── ALL SERVICES (live registry) ─────── */}
        {tab === "all-services" && (
          <section style={S.panel} data-testid="uganda-phase2-section-services">
            <h2 style={S.heading}>
              Canonical Public Service Names — Uganda Included
              <span style={S.countChip} data-testid="uganda-phase2-services-count">
                {activations.length || status?.service_count || 0} services
              </span>
            </h2>
            <p style={S.bodySmall}>
              Backend-anchored registry. Each row carries an HMAC <code style={S.code}>evidence_os_anchor</code>{" "}
              and a Phase 2 status. Human approval is required by default.
            </p>
            <div style={S.grid}>
              {activations.length === 0 && (
                <div style={S.empty}>No activations yet. Use <b>Re-seed</b> to populate the registry.</div>
              )}
              {activations.map((a) => (
                <div key={a.activation_id} style={S.card} data-testid={`uganda-phase2-activation-${a.activation_layer.toLowerCase()}`}>
                  <div style={S.cardHead}>
                    <strong style={S.gold}>{a.service_name}</strong>
                    <span style={a.status === "LIVE_ACTIVE" ? S.greenPill : S.purplePill}>{a.status}</span>
                  </div>
                  <p style={S.bodySmall}>{a.operational_goal}</p>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>LAYER</span>
                    <code style={S.code}>{a.activation_layer}</code>
                  </div>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>ANCHOR</span>
                    <code style={S.code} title={a.evidence_os_anchor}>{(a.evidence_os_anchor || "").slice(0, 18)}…</code>
                  </div>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>ID</span>
                    <code style={S.code}>{a.activation_id}</code>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ─────── FDCE ─────── */}
        {tab === "fdce" && (
          <section style={S.panel} data-testid="uganda-phase2-section-fdce">
            <h2 style={S.heading}>Forward Deployed Continuity Engineers™ Uganda</h2>
            <div style={S.commandBox}>
              Deploy CareLogix · Configure workflows · Connect labs · Connect pharmacies · Connect RPM ·
              Connect transportation · Configure CareSupply · Configure Family Safety · Configure CareThreads ·
              Configure EvidenceOS · Train staff · Monitor adoption · Drive closure rates
            </div>
            <div style={S.noticeBox}>Success Metric: Not software sold. Continuity achieved.</div>
            <p style={S.bodySmall}>
              FDCE Uganda is mounted as <code style={S.code}>FDCE</code> in the activation registry above and
              tied to EvidenceOS audit anchors.
            </p>
          </section>
        )}

        {/* ─────── COMPLIANCE ─────── */}
        {tab === "compliance" && (
          <section style={S.panel} data-testid="uganda-phase2-section-compliance">
            <h2 style={S.heading}>Uganda Safety + Compliance Firewall</h2>
            <p style={S.bodySmall}>
              Live guardrails sourced from <code style={S.code}>GET /api/adminone/uganda-phase2/status</code>.
              These constraints apply to every activation and every dispatch.
            </p>
            <div style={S.grid}>
              {guardrails.map((rule) => (
                <div key={rule} style={S.warningCard} data-testid={`uganda-phase2-guardrail-${rule.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  {rule}
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

const S = {
  canvas: {
    minHeight: "100vh",
    background:
      "radial-gradient(circle at top left, rgba(16,185,129,0.14), #040608 34%, #020304 100%)",
    color: "#f8fafc",
    fontFamily: "'Figtree', 'Inter', system-ui, sans-serif",
  },
  banner: {
    display: "flex", alignItems: "center", gap: 10,
    padding: "10px 22px",
    borderBottom: "1px solid rgba(16,185,129,0.18)",
    background: "linear-gradient(90deg, rgba(16,185,129,0.10), rgba(197,160,89,0.06))",
    fontFamily: "'Space Mono', monospace",
    fontSize: 12, letterSpacing: "0.04em",
  },
  bannerDot: { color: "#10b981" },
  bannerText: { color: "#d1fae5" },
  header: {
    padding: "22px 26px 18px",
    display: "flex", justifyContent: "space-between", alignItems: "flex-start",
    gap: 14, flexWrap: "wrap",
    borderBottom: "1px solid rgba(255,255,255,0.05)",
  },
  title: { color: "#c5a059", fontSize: 24, margin: 0, fontWeight: 800 },
  subtitle: { color: "#94a3b8", fontSize: 13, maxWidth: 820, marginTop: 6 },
  headerActions: { display: "flex", gap: 8, alignItems: "center" },
  livePill: {
    color: "#10b981",
    border: "1px solid rgba(16,185,129,0.35)",
    background: "rgba(16,185,129,0.10)",
    padding: "7px 12px", borderRadius: 999,
    fontSize: 10.5, fontWeight: 900, fontFamily: "monospace", letterSpacing: "0.10em",
  },
  refreshBtn: {
    appearance: "none",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.10)",
    color: "#cbd5e1",
    padding: "6px 12px", borderRadius: 8, cursor: "pointer",
    fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.10em",
  },
  reseedBtn: {
    appearance: "none",
    background: "linear-gradient(135deg, #c5a059, #ffd966)",
    border: "1px solid #c5a059",
    color: "#0c1a10",
    padding: "6px 12px", borderRadius: 8, cursor: "pointer",
    fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800, letterSpacing: "0.10em",
  },
  errBanner: {
    margin: "10px 26px 0",
    padding: "8px 12px",
    background: "rgba(239,68,68,0.10)",
    border: "1px solid rgba(239,68,68,0.30)",
    color: "#fecaca", borderRadius: 10,
    fontSize: 12.5,
  },
  infoBanner: {
    margin: "10px 26px 0",
    padding: "8px 12px",
    background: "rgba(16,185,129,0.10)",
    border: "1px solid rgba(16,185,129,0.30)",
    color: "#bbf7d0", borderRadius: 10,
    fontSize: 12.5,
  },
  tabs: {
    display: "flex", gap: 22,
    padding: "0 26px",
    background: "rgba(11,15,20,0.92)",
    borderBottom: "1px solid rgba(255,255,255,0.05)",
    overflowX: "auto",
  },
  tab: {
    background: "transparent",
    border: "none",
    borderBottom: "2px solid",
    padding: "14px 0",
    fontSize: 11.5, fontWeight: 900,
    cursor: "pointer",
    letterSpacing: "0.10em",
    fontFamily: "'Space Mono', monospace",
  },
  main: { padding: "20px 26px 40px" },
  panel: {
    background: "rgba(11,15,20,0.94)",
    border: "1px solid rgba(197,160,89,0.16)",
    borderRadius: 16,
    padding: 22,
  },
  heading: {
    color: "#e2e8f0",
    marginTop: 0,
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
    fontSize: 16,
  },
  countChip: {
    fontSize: 11,
    fontFamily: "'Space Mono', monospace",
    letterSpacing: "0.08em",
    background: "rgba(197,160,89,0.10)",
    color: "#c5a059",
    border: "1px solid rgba(197,160,89,0.28)",
    padding: "3px 9px",
    borderRadius: 999,
  },
  countryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
    gap: 12,
    marginTop: 12,
  },
  countryCard: {
    background: "#040608",
    border: "1px solid rgba(255,255,255,0.07)",
    borderRadius: 14,
    padding: 14,
    display: "flex", flexDirection: "column", gap: 6,
  },
  flag: { fontSize: 28 },
  greenPill: {
    color: "#10b981",
    background: "rgba(16,185,129,0.10)",
    border: "1px solid rgba(16,185,129,0.28)",
    padding: "3px 8px", borderRadius: 999,
    fontSize: 10.5, fontWeight: 800, width: "fit-content",
    fontFamily: "'Space Mono', monospace",
  },
  purplePill: {
    color: "#c084fc",
    background: "rgba(168,85,247,0.10)",
    border: "1px solid rgba(168,85,247,0.28)",
    padding: "3px 8px", borderRadius: 999,
    fontSize: 10.5, fontWeight: 800, width: "fit-content",
    fontFamily: "'Space Mono', monospace",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: 12,
    marginTop: 12,
  },
  card: {
    background: "#040608",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 12,
    padding: 14,
    display: "flex", flexDirection: "column", gap: 6,
  },
  cardHead: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 },
  gold:    { color: "#c5a059" },
  green:   { color: "#10b981" },
  purple:  { color: "#c084fc" },
  bodySmall: { color: "#cbd5e1", fontSize: 12.5, lineHeight: 1.5, margin: 0 },
  metaRow: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 },
  metaLabel: {
    color: "#64748b",
    fontFamily: "'Space Mono', monospace",
    fontSize: 9.5, letterSpacing: "0.12em",
  },
  code: {
    background: "rgba(197,160,89,0.10)",
    color: "#c5a059",
    padding: "2px 6px",
    borderRadius: 6,
    fontFamily: "'Space Mono', monospace",
    fontSize: 10.5,
  },
  commandBox: {
    background: "#040608",
    border: "1px solid rgba(16,185,129,0.20)",
    color: "#d1fae5",
    borderRadius: 12, padding: 16,
    lineHeight: 1.7, fontSize: 13,
  },
  noticeBox: {
    marginTop: 14,
    background: "rgba(245,158,11,0.08)",
    border: "1px solid rgba(245,158,11,0.25)",
    color: "#fde68a",
    padding: 14, borderRadius: 12, fontWeight: 700,
  },
  warningCard: {
    background: "rgba(239,68,68,0.08)",
    border: "1px solid rgba(239,68,68,0.25)",
    color: "#fecaca",
    borderRadius: 12, padding: 14, fontWeight: 700,
    fontSize: 13,
  },
  empty: {
    gridColumn: "1 / -1",
    color: "#94a3b8", textAlign: "center",
    padding: 28,
    border: "1px dashed rgba(255,255,255,0.10)",
    borderRadius: 12,
  },
};
